import { Link } from 'react-router-dom'
import { FiCheckCircle, FiUsers, FiBriefcase } from 'react-icons/fi'
import BlogLayout from '../../components/BlogLayout'

const toc = [
  { id: 'panorama', label: 'Panorama: la asesoría como pieza clave' },
  { id: 'opciones', label: 'Tres formas de gestionar control horario en una asesoría' },
  { id: 'arquitectura', label: 'Arquitectura multi-tenant: lo que debes pedir al software' },
  { id: 'flujo-trabajo', label: 'Flujo de trabajo recomendado' },
  { id: 'facturacion', label: 'Modelos de facturación al cliente final' },
  { id: 'responsabilidad', label: 'Responsabilidad legal y RGPD' },
  { id: 'oportunidad', label: 'Oportunidad comercial' },
  { id: 'faq', label: 'Preguntas frecuentes' },
]

const faqs = [
  { question: '¿Una asesoría laboral está obligada a ofrecer control horario a sus clientes?', answer: 'No. La obligación recae sobre la empresa empleadora. Pero el 80 % de las pymes españolas externaliza nómina y laboral en su asesoría, y estas son la primera línea de información sobre cumplimiento. No ofrecer la solución es ceder mercado.' },
  { question: '¿Puedo facturar el control horario a mis clientes?', answer: 'Sí, hay tres modelos habituales: revender la suscripción con margen, incluirlo en una cuota integral, o que el cliente contrate directamente al proveedor con tu código de afiliado. Cada uno tiene ventajas fiscales y de gestión.' },
  { question: '¿Tengo que firmar encargado de tratamiento RGPD con cada cliente?', answer: 'Sí. La asesoría es encargada de tratamiento del cliente (que es responsable). El proveedor del software es subencargada. Los tres documentos deben estar firmados antes de empezar a operar con datos de empleados.' },
  { question: '¿Qué pasa si un cliente me pide acceso a los datos de fichaje de un empleado?', answer: 'El cliente, como responsable, puede acceder libremente. Tú, como encargada, solo puedes acceder para las finalidades pactadas. Los empleados ejercen sus derechos ARCO ante el cliente, no ante la asesoría.' },
  { question: '¿Cómo demuestro a la inspección que cumplo con varios clientes?', answer: 'Cada cliente debe poder exportar sus propios registros desde su perfil. La asesoría no es responsable de las multas de los clientes, pero un buen proveedor te facilita generar PDFs firmados por cliente en lote.' },
]

export default function AsesoriasControlHorario() {
  return (
    <BlogLayout slug="asesorias-control-horario-clientes" toc={toc} faqs={faqs}>
      <section id="panorama" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Panorama: la asesoría como pieza clave</h2>
        <p>
          Las asesorías laborales son el primer punto de contacto del 80 % de las pymes españolas con el cumplimiento normativo. Cuando entró en vigor el RD-ley 8/2019, fueron las asesorías quienes tuvieron que explicar <Link to="/blog/registro-horario-obligatorio-espana">el alcance del registro horario obligatorio</Link>, advertir de sanciones y ayudar a sus clientes a implantarlo.
        </p>
        <p>
          Seis años después, el escenario es similar pero más complejo: <Link to="/blog/real-decreto-2026-fichaje-digital">nuevo Real Decreto de fichaje digital en tramitación</Link>, mayor presión inspectora, más teletrabajo y más jurisprudencia sobre horas extras. Una asesoría que sigue trabajando con Excels mensuales se queda atrás.
        </p>
      </section>

      <section id="opciones" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Tres formas de gestionar control horario en una asesoría</h2>
        <div className="space-y-4 my-6">
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2"><FiBriefcase className="h-5 w-5 text-blue-600" /> Opción 1: derivar al cliente</h3>
            <p className="text-sm text-gray-600 mb-2">Le dices al cliente que contrate por su cuenta cualquier solución y que te haga llegar los datos para nómina.</p>
            <p className="text-sm text-gray-500"><strong>Pros:</strong> cero gestión. <strong>Contras:</strong> cero control sobre la calidad del dato y cero ingreso adicional.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5">
            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2"><FiBriefcase className="h-5 w-5 text-blue-600" /> Opción 2: cada cliente con software propio, tú como invitada</h3>
            <p className="text-sm text-gray-600 mb-2">Cada cliente contrata su sistema y te da acceso de lectura. Tú entras a varios paneles distintos.</p>
            <p className="text-sm text-gray-500"><strong>Pros:</strong> el cliente paga. <strong>Contras:</strong> caos operativo si tienes 30+ clientes en 5 plataformas distintas.</p>
          </div>
          <div className="border border-blue-200 bg-blue-50/50 rounded-xl p-5 ring-2 ring-blue-500">
            <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2"><FiBriefcase className="h-5 w-5 text-blue-700" /> Opción 3: panel multi-cliente unificado (recomendado)</h3>
            <p className="text-sm text-blue-800 mb-2">Una sola cuenta de asesoría desde la que gestionas el control horario de todos tus clientes. Cada cliente sigue siendo dueño de sus datos pero tú tienes vista consolidada.</p>
            <p className="text-sm text-blue-700"><strong>Pros:</strong> productividad x10, oportunidad de cobro, control de calidad. <strong>Contras:</strong> requiere proveedor con arquitectura multi-tenant pensada para asesorías.</p>
          </div>
        </div>
      </section>

      <section id="arquitectura" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Arquitectura multi-tenant: lo que debes pedir al software</h2>
        <p>Para que la opción 3 sea viable, el sistema debe cumplir estos requisitos (además de <Link to="/blog/como-elegir-sistema-fichaje">los criterios generales para elegir un sistema de fichaje</Link>):</p>
        <ul className="space-y-3 my-6">
          <li className="flex items-start gap-3">
            <FiCheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div><strong>Aislamiento total entre clientes.</strong> Los datos de la empresa A nunca se mezclan con los de la B.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiCheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div><strong>Roles diferenciados.</strong> Asesoría con acceso de gestión a varias empresas, admin de empresa con acceso solo a la suya, empleado con acceso a sí mismo.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiCheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div><strong>Niveles de acceso configurables.</strong> Hay clientes que prefieren que la asesoría solo lea; otros que delegan toda la gestión.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiCheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div><strong>Switch rápido entre empresas.</strong> Sin tener que cerrar sesión y volver a entrar.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiCheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div><strong>Informes consolidados.</strong> Un PDF por cliente, generado en lote, listo para anexar a la nómina mensual.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiCheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div><strong>Facturación flexible.</strong> Posibilidad de que cada empresa pague directamente o que la asesoría centralice y refacture.</div>
          </li>
          <li className="flex items-start gap-3">
            <FiCheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div><strong>Escalabilidad sin penalización.</strong> Que añadir clientes nuevos no genere setup ni mínimos absurdos.</div>
          </li>
        </ul>
      </section>

      <section id="flujo-trabajo" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Flujo de trabajo recomendado</h2>
        <ol className="space-y-3 my-6">
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
            <div><strong>Onboarding inicial</strong>. Para cada nuevo cliente: alta de la empresa, creación de centros, alta de empleados, definición de calendario y firma del encargado de tratamiento.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
            <div><strong>Día a día gestionado por el cliente</strong>. Los empleados fichan desde su app, el admin del cliente revisa incidencias.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
            <div><strong>Cierre mensual por la asesoría</strong>. Exportación consolidada de horas trabajadas, ausencias, horas extras y bajas. Volcado a programa de nómina.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
            <div><strong>Generación de PDFs firmados</strong> por empresa para conservación de 4 años. Archivado en gestor documental.</div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
            <div><strong>Revisión trimestral de cumplimiento</strong>. Detección de empleados sin calendario, fichajes anómalos, exceso de horas extras y posibles problemas con representación legal.</div>
          </li>
        </ol>
      </section>

      <section id="facturacion" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Modelos de facturación al cliente final</h2>
        <div className="overflow-x-auto my-6">
          <table className="min-w-full text-sm border border-gray-100 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 font-semibold text-gray-900">Modelo</th>
                <th className="text-left p-3 font-semibold text-gray-900">Cómo funciona</th>
                <th className="text-left p-3 font-semibold text-gray-900">Cuándo elegirlo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr><td className="p-3 font-medium">Reventa con margen</td><td className="p-3 text-gray-600">Compras al proveedor a 1,50 € y facturas al cliente a 3,50 €</td><td className="p-3 text-gray-600">Asesorías con paquete de servicios</td></tr>
              <tr><td className="p-3 font-medium">Cuota integral</td><td className="p-3 text-gray-600">Incluyes el control horario en la cuota mensual sin desglosar</td><td className="p-3 text-gray-600">Cuando ya cobras flat fee al cliente</td></tr>
              <tr><td className="p-3 font-medium">Afiliado</td><td className="p-3 text-gray-600">El cliente contrata directo, tú cobras comisión</td><td className="p-3 text-gray-600">Cuando no quieres responsabilidad de gestión</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section id="responsabilidad" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Responsabilidad legal y RGPD</h2>
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg my-6">
          <p className="text-blue-900 text-sm font-semibold mb-2">Cadena de responsabilidades</p>
          <ul className="text-blue-800 text-sm space-y-1">
            <li>• <strong>Empresa cliente:</strong> responsable del tratamiento (art. 4.7 RGPD).</li>
            <li>• <strong>Asesoría:</strong> encargada del tratamiento (art. 4.8 RGPD).</li>
            <li>• <strong>Proveedor del software:</strong> sub-encargada del tratamiento.</li>
          </ul>
        </div>
        <ul className="space-y-2 my-4">
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Firma encargado de tratamiento con cada cliente y autorización para sub-encargada.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Confirma con el proveedor que está firmado el contrato de sub-encargada.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> Documenta los accesos de tu equipo y el motivo de cada consulta.</li>
          <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> No mezcles datos de clientes en correos generales ni copias backup.</li>
        </ul>
      </section>

      <section id="oportunidad" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Oportunidad comercial</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
          <div className="border border-gray-200 rounded-xl p-5 text-center">
            <FiUsers className="h-8 w-8 text-blue-600 mx-auto mb-3" />
            <p className="text-3xl font-extrabold text-gray-900 mb-1">+30 €</p>
            <p className="text-xs text-gray-500">Margen mensual medio por cliente con reventa</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5 text-center">
            <FiUsers className="h-8 w-8 text-blue-600 mx-auto mb-3" />
            <p className="text-3xl font-extrabold text-gray-900 mb-1">-70 %</p>
            <p className="text-xs text-gray-500">Tiempo de cierre mensual de nóminas</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-5 text-center">
            <FiUsers className="h-8 w-8 text-blue-600 mx-auto mb-3" />
            <p className="text-3xl font-extrabold text-gray-900 mb-1">x4</p>
            <p className="text-xs text-gray-500">Capacidad de cliente atendido por gestor</p>
          </div>
        </div>
        <p>
          Para una asesoría con 40 clientes, el control horario digitalizado puede suponer entre 12.000 y 18.000 € de ingreso recurrente anual extra y reducir las horas mensuales de cierre de nómina en un 60-70 %.
        </p>
      </section>

      <section id="faq" className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Preguntas frecuentes</h2>
        <div className="space-y-6">
          {faqs.map((f) => (
            <div key={f.question}>
              <h3 className="font-semibold text-gray-900 mb-1">{f.question}</h3>
              <p className="text-gray-600 text-sm">{f.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="border-t border-gray-100 pt-8 mt-12 text-sm text-gray-500">
        ¿Tu asesoría quiere probar PresenzIA con sus clientes?
        <Link to="/registro" className="text-blue-600 hover:underline mx-1">Crear cuenta gratis</Link>
        ·
        <Link to="/precios" className="text-blue-600 hover:underline mx-1">ver planes</Link>.
      </div>
    </BlogLayout>
  )
}
